/**
 * Derived values for the Ayarlar screens: the setup checklist (Kurulum 1 / 19), group
 * summaries, row actions, labels and the settings search. Pure functions over Settings.
 */

import type {
  Connection,
  ConnectionGroupId,
  ConnectionStatus,
  NumberPolicy,
  NumberPolicyId,
  RichText,
  ScopeAccess,
  Settings,
  TokenHolder,
} from '../api/settings.js';
import { formatUsd, formatUsdNumber } from '../lib/format.js';
import type { SettingsTab } from './router.js';

export const STATUS_LABELS: Record<ConnectionStatus, string> = {
  connected: 'Bağlı',
  not_connected: 'Bağlı değil',
  awaiting_sercan: 'Senden bekleniyor',
  applying: 'Başvuru sürüyor',
  expiring: 'Süresi doluyor',
  error: 'Hata',
  disabled: 'Kapalı',
};

export const HOLDER_LABELS: Record<TokenHolder, string> = { executor: 'Dağıtıcı', ingest: 'İçe alma işçisi' };

export function holderLabel(holder: TokenHolder | null): string {
  return holder ? HOLDER_LABELS[holder] : 'Yok';
}

export const ACCESS_LABELS: Record<ScopeAccess, string> = {
  read: 'okuma',
  write: 'yazma',
  append: 'ekleme',
  use: 'kullanım',
};

export const DEFAULT_T4_HOLD_MS = 1_500;

export function statusCounts(connections: readonly Connection[]): Record<ConnectionStatus, number> {
  const counts: Record<ConnectionStatus, number> = {
    connected: 0,
    not_connected: 0,
    awaiting_sercan: 0,
    applying: 0,
    expiring: 0,
    error: 0,
    disabled: 0,
  };
  for (const connection of connections) counts[connection.status] += 1;
  return counts;
}

/** A connection with a live grant: counted as ready and revocable. */
export function isLive(connection: Connection): boolean {
  return connection.status === 'connected' || connection.status === 'expiring';
}

export function revocable(connection: Connection): boolean {
  return isLive(connection) || connection.status === 'error';
}

/** Needs Sercan to act (as opposed to waiting on a third party or switched off). */
export function needsSercan(connection: Connection): boolean {
  return (
    connection.status === 'awaiting_sercan' || connection.status === 'not_connected' || connection.status === 'error'
  );
}

export type SegmentKind = 'done' | 'hatch' | 'accent' | 'danger' | 'dashed';

export interface SetupSummary {
  /** Connections that count: everything that is not switched off by policy. */
  total: number;
  ready: number;
  counts: Record<ConnectionStatus, number>;
  /** Bar segments, in drawing order. */
  segments: { id: string; kind: SegmentKind }[];
  /** Legend and spoken parts, zero counts left out. */
  legend: { kind: SegmentKind; text: string; spoken: string }[];
  /** Remaining steps in the setup order. */
  steps: Connection[];
  next: Connection | null;
  /** 1-based position of the next step in the remaining list. */
  nextIndex: number;
}

const LEGEND: { status: ConnectionStatus; kind: SegmentKind; text: string; spoken: string }[] = [
  { status: 'connected', kind: 'done', text: 'bağlı', spoken: 'bağlı' },
  { status: 'expiring', kind: 'done', text: 'süresi doluyor', spoken: 'süresi doluyor' },
  { status: 'applying', kind: 'hatch', text: 'başvuruda', spoken: 'başvuruda' },
  { status: 'awaiting_sercan', kind: 'accent', text: 'senden', spoken: 'senden bekleniyor' },
  { status: 'error', kind: 'danger', text: 'hata', spoken: 'hata' },
  { status: 'not_connected', kind: 'dashed', text: 'bağlı değil', spoken: 'bağlı değil' },
];

function setupOrder(connection: Connection): number {
  return connection.setup?.order ?? Number.MAX_SAFE_INTEGER;
}

export function setupSummary(settings: Settings): SetupSummary {
  const counted = settings.connections.filter((connection) => connection.status !== 'disabled');
  const counts = statusCounts(settings.connections);
  const segments: SetupSummary['segments'] = [];
  const legend: SetupSummary['legend'] = [];
  for (const item of LEGEND) {
    const count = counts[item.status];
    for (let index = 0; index < count; index += 1) segments.push({ id: `${item.status}-${index}`, kind: item.kind });
    if (count > 0) legend.push({ kind: item.kind, text: `${count} ${item.text}`, spoken: `${item.spoken} ${count}` });
  }
  const steps = counted.filter((connection) => !isLive(connection)).sort((a, b) => setupOrder(a) - setupOrder(b));
  const nextPosition = steps.findIndex(needsSercan);
  return {
    total: counted.length,
    ready: counts.connected + counts.expiring,
    counts,
    segments,
    legend,
    steps,
    next: nextPosition >= 0 ? (steps[nextPosition] ?? null) : null,
    nextIndex: nextPosition + 1,
  };
}

export function setupBarLabel(summary: SetupSummary): string {
  return `Kurulum: ${summary.total} bağlantı; ${summary.legend.map((part) => part.spoken).join(', ')}`;
}

export interface SummaryPart {
  text: string;
  spoken: string;
  tone: 'danger' | 'accent' | 'plain';
  triangle: boolean;
}

export interface GroupSummary {
  id: ConnectionGroupId;
  title: string;
  connections: Connection[];
  parts: SummaryPart[];
  /** Up to two parts for the phone title-block cells. */
  phoneParts: SummaryPart[];
  desktopSuffix: string | null;
  spoken: string;
}

const GROUP_PARTS: { status: ConnectionStatus; text: string; spoken: string; tone: SummaryPart['tone'] }[] = [
  { status: 'error', text: 'hata', spoken: 'hata', tone: 'danger' },
  { status: 'expiring', text: 'süresi doluyor', spoken: 'süresi doluyor', tone: 'plain' },
  { status: 'awaiting_sercan', text: 'senden', spoken: 'senden bekleniyor', tone: 'accent' },
  { status: 'applying', text: 'başvuruda', spoken: 'başvuruda', tone: 'plain' },
  { status: 'connected', text: 'bağlı', spoken: 'bağlı', tone: 'plain' },
  { status: 'not_connected', text: 'bağlı değil', spoken: 'bağlı değil', tone: 'plain' },
  { status: 'disabled', text: 'kapalı', spoken: 'kapalı', tone: 'plain' },
];

export function groupSummaries(settings: Settings): GroupSummary[] {
  return settings.groups.map((group) => {
    const connections = settings.connections.filter((connection) => connection.group === group.id);
    const counts = statusCounts(connections);
    const parts: SummaryPart[] = [];
    for (const connection of connections) {
      if (connection.alert && !isLive(connection)) {
        parts.push({ text: connection.alert.short, spoken: connection.alert.short, tone: 'danger', triangle: true });
      }
    }
    for (const item of GROUP_PARTS) {
      const count = counts[item.status];
      if (count > 0) {
        parts.push({
          text: `${count} ${item.text}`,
          spoken: `${count} ${item.spoken}`,
          tone: item.tone,
          triangle: item.status === 'error',
        });
      }
    }
    // Two cells side by side: only the first part keeps its colour, so one thing stands out.
    const phoneParts = parts
      .slice(0, 2)
      .map((part, index) => (index > 0 && parts[0]?.tone !== 'plain' ? { ...part, tone: 'plain' as const } : part));
    if (phoneParts.length < 2 && group.phoneNote) {
      phoneParts.push({ text: group.phoneNote, spoken: group.phoneNote, tone: 'plain', triangle: false });
    }
    return {
      id: group.id,
      title: group.title,
      connections,
      parts,
      phoneParts,
      desktopSuffix: group.desktopSuffix,
      spoken: `${group.title}, ${connections.length} bağlantı: ${parts.map((part) => part.spoken).join(', ')}`,
    };
  });
}

/** Claude's line uses the live GM budget, so a budget change shows up here too. */
export function connectionPurpose(settings: Settings, connection: Connection): RichText {
  if (connection.usage) {
    const budget = policyById(settings, connection.usage.budgetPolicy);
    const cap = budget ? formatUsd(budget.value, 'auto') : '';
    return [
      'bu ay ',
      { mono: `${formatUsdNumber(connection.usage.monthApiEquivalent)} / ${cap}` },
      ' API karşılığı · faturalanmaz',
    ];
  }
  return connection.purpose;
}

/**
 * The only thing Bağla may open: the Twin backend's own connect start for one connection,
 * `/api/connections/:id/connect` on the panel's origin. Anything else (another host, a
 * protocol-relative or backslash path such as `/\host`, a script URL) is not a link, so a
 * bad value in the data can never send the consent flow past the executor.
 */
const CONNECT_PATH = /^\/api(?:\/v1)?\/connections\/[a-z0-9][a-z0-9-]*\/connect$/;

export function safeConnectUrl(href: string | null): string | null {
  return href !== null && CONNECT_PATH.test(href) ? href : null;
}

/**
 * Third-party pages (application forms, setup docs, the repo): absolute https only, and never
 * with a user name or password in the address.
 */
export function safeHttpsUrl(href: string | null): string | null {
  if (!href) return null;
  try {
    const url = new URL(href);
    return url.protocol === 'https:' && url.username === '' && url.password === '' ? url.href : null;
  } catch {
    return null;
  }
}

export type ConnectState =
  | { kind: 'connect'; url: string; label: 'Bağla' | 'Yenile' | 'Yeniden bağla' }
  | { kind: 'blocked'; reason: string }
  | { kind: 'none' };

/** Whether Bağla can open the provider's consent page right now, and why not. */
export function connectState(connection: Connection): ConnectState {
  if (connection.status === 'disabled')
    return { kind: 'blocked', reason: connection.disabledReason ?? 'politika kapalı' };
  if (connection.status === 'applying') {
    return { kind: 'blocked', reason: connection.connectBlockedReason ?? 'başvuru sürüyor' };
  }
  const url = safeConnectUrl(connection.connectUrl);
  // Bağla starts this connection's own flow, never another connection's.
  if (
    !url ||
    (url !== `/api/connections/${connection.id}/connect` && url !== `/api/v1/connections/${connection.id}/connect`)
  ) {
    return { kind: 'none' };
  }
  if (connection.status === 'expiring') return { kind: 'connect', url, label: 'Yenile' };
  if (connection.status === 'connected') return { kind: 'connect', url, label: 'Yeniden bağla' };
  return { kind: 'connect', url, label: 'Bağla' };
}

export type RowAction =
  | { kind: 'connect'; url: string; label: 'Bağla' | 'Yenile' }
  | { kind: 'apply'; url: string }
  | { kind: 'details' };

/** The one primary action of a connection row. */
export function rowAction(connection: Connection): RowAction {
  const state = connectState(connection);
  if (connection.action === 'apply') {
    const url = safeHttpsUrl(connection.applyUrl);
    if (url) return { kind: 'apply', url };
  }
  if ((connection.action === 'connect' || connection.action === 'renew') && state.kind === 'connect') {
    return { kind: 'connect', url: state.url, label: state.label === 'Yenile' ? 'Yenile' : 'Bağla' };
  }
  return { kind: 'details' };
}

export function policyById(settings: Settings, id: NumberPolicyId): NumberPolicy | undefined {
  return settings.policies.find((policy) => policy.id === id);
}

export function pendingSuggestions(settings: Settings): NumberPolicy[] {
  return settings.policies.filter((policy) => policy.suggestion !== null);
}

export function t4HoldMs(settings: Settings | null): number {
  return (settings && policyById(settings, 'approval_t4_hold')?.value) || DEFAULT_T4_HOLD_MS;
}

export function agentsSummary(settings: Settings): string {
  const blocked = settings.agents.filter((agent) => agent.blockedBy !== null).length;
  if (blocked === settings.agents.length) return `${settings.agents.length} tanımlı · bağlantı bekliyor`;
  const enabled = settings.agents.filter((agent) => agent.enabled).length;
  return `${settings.agents.length} tanımlı · ${enabled} açık`;
}

export function backupMissing(settings: Settings): boolean {
  return settings.backup.lastBackupAt === null;
}

export function killSwitchUntested(settings: Settings): boolean {
  return settings.security.killSwitchTest.lastTestedAt === null;
}

export const TAB_TITLES: Record<SettingsTab, string> = {
  kurulum: 'Kurulum',
  baglantilar: 'Bağlantılar',
  'api-kullanimi': 'API kullanımı',
  politika: 'Politika',
  butceler: 'Bütçeler',
  'onay-sureleri': 'Onay süreleri',
  'sert-sinirlar': 'Sert sınırlar',
  ajanlar: 'Ajanlar',
  takvim: 'Takvim',
  tercihler: 'Tercihler',
  bildirimler: 'Bildirimler',
  dil: 'Dil ve bölge',
  veri: 'Veri ve saklama',
  guvenlik: 'Güvenlik',
  hakkinda: 'Hakkında',
  degisiklikler: 'Değişiklik kaydı',
};

/** Desktop sub-navigation, in the artboard order. */
export const DESKTOP_TABS: readonly SettingsTab[] = [
  'kurulum',
  'baglantilar',
  'api-kullanimi',
  'politika',
  'sert-sinirlar',
  'ajanlar',
  'takvim',
  'tercihler',
  'veri',
  'guvenlik',
  'hakkinda',
];

/** Which desktop tab a route shows: sub-pages open inside their parent tab. */
export function desktopTab(tab: SettingsTab | null): SettingsTab {
  switch (tab) {
    case null:
      return 'baglantilar';
    case 'butceler':
    case 'onay-sureleri':
      return 'politika';
    case 'bildirimler':
    case 'dil':
      return 'tercihler';
    default:
      return tab;
  }
}

export interface SearchResult {
  id: string;
  label: string;
  detail: string;
  to: string;
}

const SECTION_ENTRIES: { label: string; detail: string; tab: SettingsTab }[] = [
  { label: 'Kurulum', detail: 'kurulum sırası', tab: 'kurulum' },
  { label: 'Bağlantılar', detail: 'hizmetler ve yetkiler', tab: 'baglantilar' },
  { label: 'API kullanımı', detail: 'bugün, bu ay, kalan, son 30 gün', tab: 'api-kullanimi' },
  { label: 'Bütçeler', detail: 'politika', tab: 'butceler' },
  { label: 'Onay süreleri', detail: 'geri alma, onay ömrü, basılı tutma', tab: 'onay-sureleri' },
  { label: 'İndirim tavanı', detail: 'politika', tab: 'politika' },
  { label: 'Mail otonomisi', detail: 'politika', tab: 'politika' },
  { label: 'Günlük gönderim tavanı', detail: 'politika', tab: 'politika' },
  { label: 'Özellik anahtarları', detail: 'lead taraması, dış temas, sosyal, kuru çalışma', tab: 'politika' },
  { label: 'Sert sınırlar', detail: 'kodda, salt okunur', tab: 'sert-sinirlar' },
  { label: 'Ajanlar', detail: 'model, bütçe, son çalışma', tab: 'ajanlar' },
  { label: 'Takvim', detail: 'günlük ritim', tab: 'takvim' },
  { label: 'Tema', detail: 'Sistem, Açık, Koyu', tab: 'tercihler' },
  { label: 'Bildirimler', detail: 'Telegram, raporlar, sessiz saatler', tab: 'bildirimler' },
  { label: 'Sessiz saatler', detail: 'bildirimler', tab: 'bildirimler' },
  { label: 'Dil ve bölge', detail: 'dil, saat dilimi, para gösterimi', tab: 'dil' },
  { label: 'Veri ve saklama', detail: 'saklama süreleri, yedek', tab: 'veri' },
  { label: 'Güvenlik', detail: 'oturum, denetim zinciri, acil durdurma', tab: 'guvenlik' },
  { label: 'Hakkında', detail: 'sürüm, commit, CI', tab: 'hakkinda' },
  { label: 'Değişiklik kaydı', detail: 'kim, ne zaman, gerekçe', tab: 'degisiklikler' },
];

function fold(text: string): string {
  return text.toLocaleLowerCase('tr');
}

/** Settings search: sections, connections, budgets and hard limits whose words match. */
export function searchSettings(settings: Settings, query: string): SearchResult[] {
  const needle = fold(query.trim());
  if (needle === '') return [];
  const results: SearchResult[] = [];
  for (const entry of SECTION_ENTRIES) {
    results.push({ id: `s-${entry.label}`, label: entry.label, detail: entry.detail, to: `/ayarlar/${entry.tab}` });
  }
  for (const connection of settings.connections) {
    results.push({
      id: `c-${connection.id}`,
      label: connection.name,
      detail: `Bağlantı · ${STATUS_LABELS[connection.status]}`,
      to: `/ayarlar/baglanti/${connection.id}`,
    });
  }
  for (const policy of settings.policies.filter((item) => item.group === 'budget')) {
    results.push({
      id: `p-${policy.id}`,
      label: `${policy.label} bütçesi`,
      detail: 'Bütçeler',
      to: '/ayarlar/butceler',
    });
  }
  for (const limit of settings.hardLimits) {
    results.push({ id: `h-${limit.id}`, label: limit.spoken, detail: 'Sert sınır', to: '/ayarlar/sert-sinirlar' });
  }
  return results.filter((result) => fold(`${result.label} ${result.detail}`).includes(needle)).slice(0, 12);
}
